import { UtilService } from 'src/app/core/services/util/util.service';
import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef, AfterViewChecked, Type, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AppGlobalsModule, AppGlobalsModule as _global } from '../app-globals.module';
import { BreakPointSizeService } from '../core/services/break-point-size/break-point-size.service';
import { I18nService } from '../core/services/i18n/i18n.service';
import { UrlAddressService } from '../core/services/url-address/url-address.service';
import { btnLink } from '../shared/helpers/btn-link/btn-link.model';
import { ShapeTypes } from '../shared/shape-divider/shape-types.enum';
import { BrowserPlatformService } from '../core/services/browser-platform/browser-platform.service';

type IdleCallbackWindow = Window & {
    requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
    cancelIdleCallback?: (handle: number) => void;
};

// HOTFIXES:
// Allows css infiltration to import from here the stylse of the whole angular,
// and not through angualr.json because in universal mobile it fails to load it
@Component({
    selector: 'app-site-footer',
    templateUrl: './site-footer.component.html',
    styleUrls: ['./site-footer.component.scss'],
    standalone: false,
})
export class SiteFooterComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
    private _isHeb: boolean;
    public shapeTypes = ShapeTypes;
    private _langBaseUrl: string = '';
    public isAngularApp = false;
    private _subscriptions: Subscription[] = [];
    public globals = AppGlobalsModule;
    public showDeferredWidgets = signal(false);
    public additionalContentComponent = signal<Type<unknown> | null>(null);
    public additionalContentModule = signal<Type<unknown> | null>(null);
    public chatbotComponent = signal<Type<unknown> | null>(null);
    //nihul is a work area - the chat must not exist there at all (not even its chunk)
    public inNihul = signal(false);
    public copyFeedback = signal('');
    private deferredWidgetsTimeoutId: number | null = null;
    private deferredWidgetsIdleCallbackId: number | null = null;
    private copyFeedbackTimeoutId: number | null = null;
    private readonly deferredWidgetsDelayMs = 6000;
    private readonly deferredWidgetsIdleTimeoutMs = 2000;

    public i18n = inject(I18nService);
    public breakPointSizeService = inject(BreakPointSizeService);
    private _urlAddress = inject(UrlAddressService);
    private _utils = inject(UtilService);
    private _browser = inject(BrowserPlatformService);
    private _router = inject(Router);
    private changeDetector = inject(ChangeDetectorRef);

    constructor() {
        this._isHeb = this.i18n.langIsHeb();
    }

    private viewCheckedTimes = 0;
    ngAfterViewChecked(): void {
        if (AppGlobalsModule.isDevelopment || this._utils.isStaging()) {
            if (this.viewCheckedTimes++ % 500 === 499) {
                console.error('If you see this message, the application has a critical performence issue - ask Remy for help!');
            }
        }
    }

    ngOnInit(): void {
        this._langBaseUrl = this.i18n.getLangBaseUrl();
        this.matLinks = this._getMatLinks();
    }

    ngAfterViewInit(): void {
        this._subscriptions.push(
            this._urlAddress
                .getIsAngularApp()
                .pipe(take(1))
                .subscribe((isAngularApp) => {
                    this.isAngularApp = isAngularApp;

                    // Manually call to avoid Error: NG0100: ExpressionChangedAfterItHasBeenCheckedError
                    this.changeDetector.detectChanges();
                }),
        );

        //follow in-app navigation too, so entering /nihul destroys the chat and leaving restores it
        this._subscriptions.push(this._router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => this._updateInNihul()));
        this._updateInNihul();

        this._scheduleDeferredWidgets();
    }

    private _updateInNihul(): void {
        //getPathname is SSR-aware (nativeWindow is not) AND a real path on the client - the
        //server must also render no footer for /nihul, or a hard load flashes it until hydration
        const pathname = this._urlAddress.getPathname().toLowerCase();
        this.inNihul.set(/^\/nihul($|[/?#])/.test(pathname));
        //left nihul after the deferred load already ran there - load the widgets now
        if (!this.inNihul() && this.showDeferredWidgets() && (!this.chatbotComponent() || !this.additionalContentComponent())) {
            void this._loadDeferredWidgets();
        }
    }

    public async copyText(value: string): Promise<void> {
        const clipboard = this._browser.navigator?.clipboard;
        if (!value || !clipboard) {
            return;
        }

        try {
            await clipboard.writeText(value);
            this.copyFeedback.set(this.copiedToClipboard);

            if (this.copyFeedbackTimeoutId !== null) {
                clearTimeout(this.copyFeedbackTimeoutId);
            }

            this.copyFeedbackTimeoutId = this._browser.setTimeout(() => {
                this.copyFeedback.set('');
                this.copyFeedbackTimeoutId = null;
            }, 3000);
        } catch {
            this.copyFeedback.set('');
        }
    }

    public matLinks: btnLink[][] = [];
    private _getMatLinks() {
        return [this._getBtnLinksByName('page'), this._getBtnLinksByName('mapWebsite'), this._getBtnLinksByName('contactUs')];
    }
    private D_N = $localize`:@@DN-mizrahBinyamin:ד.נ מזרח בנימין` + ', 90628';

    public yeahivaDetails: { name: string; value: string; iconName: string }[] = [
        { name: this.D_N, value: this.D_N, iconName: 'mail' },
        { name: $localize`:@@phone:טלפון` + ': 02-9975192', value: '02-9975192', iconName: 'phone' },
        { name: $localize`:@@fax:פקס` + ': 02-9975385', value: '02-9975385', iconName: 'print' },
    ];
    public copyToClipboard = $localize`:@@copyToClipboard:העתק ללוח`;
    public copiedToClipboard = $localize`:@@copiedToClipboard:הועתק ללוח`;

    private _getBtnLinksByName(name: string): btnLink[] {
        let btnLinks: btnLink[] = [];
        switch (name) {
            case 'page':
                btnLinks = [
                    { name: $localize`:@@yeshiva:ישיבה`, link: '/' },
                    { name: $localize`:@@beitMidrash:בית מדרש`, link: '/midrash', isAngularPage: true },
                    { name: $localize`:@@askTheRabbi:שאל את הרב`, link: '/ask', isAngularPage: true },
                    { name: $localize`:@@calendar:לוח שנה`, link: '/calendar' },
                    {
                        name: $localize`:@@date-converter:ממיר תאריכים עברי לועזי`,
                        link: '/calendar/dateconverter',
                        isAngularPage: true,
                    },
                    { name: $localize`:@@monthTimes:זמני החודש`, link: '/calendar/timestable' },
                    { name: $localize`:@@wiki:ויקישיבה`, link: '/wiki' },
                    { name: $localize`:@@myYeshiva:הישיבה שלי`, link: '/users' },
                    {
                        name: this._isHeb ? 'English' : 'עברית',
                        link: _global.otherLanguageHost(this.i18n.getLang()),
                        className: (this._isHeb ? 'eng' : 'heb') + '-letters',
                        iconName: 'language',
                    },
                ];
                break;
            case 'mapWebsite':
                btnLinks = [
                    { name: $localize`:@@siteMap:מפת האתר`, link: '/' },
                    {
                        name: $localize`:@@familyPurity:בית הוראה לטהרת המשפחה`,
                        link: (this._isHeb ? '' : this._langBaseUrl) + '/ask/askhoraa/?inner=2',
                    },
                    { name: $localize`:@@shabatTimes:זמני כניסת השבת`, link: '/calendar/shabatot' },
                    {
                        name: $localize`:@@parashatHashavua:פרשת השבוע`,
                        link: '/tags/' + $localize`:@@parashatHashavua:פרשת השבוע`,
                    },
                    { name: $localize`:@@askYourQuestion:שליחת שאלה`, link: '/ask/ask' },
                    { name: $localize`:@@daily-halacha:הלכה יומית`, link: '/ask/halacha' },
                    {
                        name: $localize`:@@pnineiHalacha:פניני הלכה`,
                        link: _global.categoryLink(149),
                    },
                    {
                        name: $localize`:@@talmudicEncyclopedia:האנציקלופדיה התלמודית`,
                        link: (this._isHeb ? '' : this._langBaseUrl) + '/wiki/index.php/ויקישיבה:אנציקלופדיה_תלמודית',
                    },
                ];
                break;
            case 'contactUs':
                btnLinks = [
                    { name: $localize`:@@contact:צור קשר`, link: '/contact' },
                    {
                        name: $localize`:@@advertiseWithUs:פרסם אצלנו`,
                        link: (this._isHeb ? '' : this._langBaseUrl) + '/about/advertise',
                    },
                    { name: $localize`:@@web-development:בניית אתרים`, link: '/build' },
                    { name: $localize`:@@writeUs:כתוב לנו`, link: '/contact' },
                    {
                        name: $localize`:@@timesOnYourSite:זמני היום באתרך`,
                        link: '/generalpages/externalcontent?the_content=time',
                    },
                    { name: $localize`:@@termsAndConditions:תקנון`, link: '/terms', isAngularPage: true },
                    { name: $localize`:@@donations-dedications:תרומות והקדשות`, link: '/dedication' },
                ];
                break;
        }
        return btnLinks;
    }

    ngOnDestroy() {
        if (this.deferredWidgetsTimeoutId !== null) {
            clearTimeout(this.deferredWidgetsTimeoutId);
        }

        const win = this._browser.nativeWindow as IdleCallbackWindow | null;
        if (this.deferredWidgetsIdleCallbackId !== null && typeof win?.cancelIdleCallback === 'function') {
            win.cancelIdleCallback(this.deferredWidgetsIdleCallbackId);
        }
        if (this.copyFeedbackTimeoutId !== null) {
            clearTimeout(this.copyFeedbackTimeoutId);
        }
        this._utils.unsubscribe(this._subscriptions);
    }

    private _scheduleDeferredWidgets(): void {
        const win = this._browser.nativeWindow as IdleCallbackWindow | null;

        if (!win || this.showDeferredWidgets()) {
            return;
        }

        const showWidgets = () => {
            this.deferredWidgetsTimeoutId = null;
            this.deferredWidgetsIdleCallbackId = null;
            this.showDeferredWidgets.set(true);
            void this._loadDeferredWidgets();
        };

        this.deferredWidgetsTimeoutId = win.setTimeout(() => {
            this.deferredWidgetsTimeoutId = null;

            if (typeof win.requestIdleCallback === 'function') {
                this.deferredWidgetsIdleCallbackId = win.requestIdleCallback(() => showWidgets(), {
                    timeout: this.deferredWidgetsIdleTimeoutMs,
                });
                return;
            }

            showWidgets();
        }, this.deferredWidgetsDelayMs);
    }

    private async _loadDeferredWidgets(): Promise<void> {
        await Promise.all([this._loadAdditionalContent(), this._loadChatbot()]);
    }

    private async _loadAdditionalContent(): Promise<void> {
        //no footer features in the nihul; _updateInNihul loads it if we ever leave
        if ((this.additionalContentComponent() && this.additionalContentModule()) || this.inNihul()) {
            return;
        }

        const [{ AdditionalContentComponent }, { DeferredAdditionalContentModule }] = await Promise.all([
            import('../shared/additional-content/additional-content.component'),
            import('../shared/additional-content/deferred-additional-content.module'),
        ]);
        this.additionalContentComponent.set(AdditionalContentComponent);
        this.additionalContentModule.set(DeferredAdditionalContentModule);
        this.changeDetector.markForCheck();
    }

    private async _loadChatbot(): Promise<void> {
        //in nihul don't even download the chunk; _updateInNihul loads it if we ever leave
        if (this.chatbotComponent() || this.inNihul()) {
            return;
        }

        const { ChatbotComponent } = await import('../general-pages/chatbot/chatbot.component');
        this.chatbotComponent.set(ChatbotComponent);
        this.changeDetector.markForCheck();
    }
}
